import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';

import type { UserConsentItem } from '../../services/consent.rest.service';
import { createScopedSelector } from '../../../../shared/utils/factory.utils';
import { GeneralActions } from '../../../../shared/redux/general/general.actions';
import { MenuActions } from '../../redux/menu.action';

import { ConsentActions } from './consent.actions';

export const CONSENT_STORE_KEY = 'consent';

// state
export interface ConsentState {
  isConsentModalOpen: boolean;
  consentModalMode: 'onboarding' | 'update' | null;
  /** Working copy — mutated by toggleConsentItem while the modal is open. */
  userConsents: Array<UserConsentItem>;
  /** Last backend truth. userConsents is reset to this whenever the modal opens or closes without submitting. */
  serverConsents: Array<UserConsentItem>;
  userConsentsLoading: boolean;
  isConsentUpdateSubmitting: boolean;
}

export const initialConsentState: ConsentState = {
  isConsentModalOpen: false,
  consentModalMode: null,
  userConsents: [],
  serverConsents: [],
  userConsentsLoading: false,
  isConsentUpdateSubmitting: false,
};

// selectors
const selectConsentState = createFeatureSelector<ConsentState>(CONSENT_STORE_KEY);
const createConsentSelector = createScopedSelector(selectConsentState);

// reducer
export const consentReducer = createReducer(
  initialConsentState,

  // MenuActions.menuEntered and GeneralActions.appResumed are cross-domain triggers;
  // reducers may freely listen to any action per team convention.
  on(
    MenuActions.menuEntered,
    GeneralActions.appResumed,
    ConsentActions.missingConsentsRequested,
    (state): ConsentState => ({ ...state, userConsentsLoading: true })
  ),

  on(
    MenuActions.menuEnteredSucceeded,
    ConsentActions.appResumedSucceeded,
    ConsentActions.missingConsentsRequestedSucceeded,
    (state, { consents }): ConsentState => {
      // Keeps serverConsents in sync with the latest backend truth.
      // Only updates the working copy when the modal is closed — avoids
      // overwriting in-progress user selections during a background refresh.
      // Also auto-opens the modal in onboarding mode when mandatory consents are missing.
      const hasMissingMandatory = consents.some(c => !c.optional && !c.consented);
      const shouldAutoOpen = hasMissingMandatory && !state.isConsentModalOpen;
      return {
        ...state,
        serverConsents: consents,
        userConsents: state.isConsentModalOpen ? state.userConsents : consents,
        userConsentsLoading: false,
        isConsentModalOpen: shouldAutoOpen || state.isConsentModalOpen,
        consentModalMode: shouldAutoOpen ? 'onboarding' : state.consentModalMode,
      };
    }
  ),

  on(
    MenuActions.menuEnteredFailed,
    ConsentActions.appResumedFailed,
    ConsentActions.missingConsentsRequestedFailed,
    (state): ConsentState => ({ ...state, userConsentsLoading: false })
  ),

  // Spread serverConsents into a fresh array so userConsents is a disconnected
  // copy — prevents toggles from ever mutating the serverConsents reference.
  on(
    ConsentActions.openConsentModal,
    (state): ConsentState => ({
      ...state,
      isConsentModalOpen: true,
      consentModalMode: 'update',
      userConsents: [...state.serverConsents],
    })
  ),

  on(
    ConsentActions.closeConsentModal,
    (state): ConsentState => ({
      ...state,
      isConsentModalOpen: false,
      consentModalMode: null,
    })
  ),

  on(
    ConsentActions.toggleConsentItem,
    (state, { id }): ConsentState => ({
      ...state,
      userConsents: state.userConsents.map(item => (item.id === id ? { ...item, consented: !item.consented } : item)),
    })
  ),

  on(ConsentActions.submitConsentUpdate, (state): ConsentState => ({ ...state, isConsentUpdateSubmitting: true })),
  on(
    ConsentActions.submitConsentUpdateSucceeded,
    (state): ConsentState => ({
      ...state,
      isConsentModalOpen: false,
      consentModalMode: null,
      isConsentUpdateSubmitting: false,
      // Sync serverConsents so reopening the modal before the next fetch reflects
      // what was actually submitted.
      serverConsents: state.userConsents,
    })
  ),
  on(ConsentActions.submitConsentUpdateFailed, (state): ConsentState => ({ ...state, isConsentUpdateSubmitting: false })),

  on(GeneralActions.logout, (state): ConsentState => ({ ...state, isConsentModalOpen: false, consentModalMode: null }))
);

export const ConsentSelectors = {
  isConsentModalOpen: createConsentSelector((state: ConsentState) => state.isConsentModalOpen),
  consentModalMode: createConsentSelector((state: ConsentState) => state.consentModalMode),
  userConsents: createConsentSelector((state: ConsentState) => state.userConsents),
  isUserConsentsLoading: createConsentSelector((state: ConsentState) => state.userConsentsLoading),
  isConsentUpdateSubmitting: createConsentSelector((state: ConsentState) => state.isConsentUpdateSubmitting),
};

/** True when all mandatory (non-optional) consents have been given. */
export const isMandatoryConsentComplete = createSelector(selectConsentState, (state: ConsentState) =>
  state.userConsents.filter(c => !c.optional).every(c => c.consented)
);

/** True when at least one optional consent has NOT been checked. */
export const hasUncheckedOptionalConsents = createSelector(selectConsentState, (state: ConsentState) =>
  state.userConsents.some(c => c.optional && !c.consented)
);
